/**
 * Reimagine (Norton ODS) theme helper.
 *
 * The Reimagine re-theme is applied by setting `<html data-theme="norton-reimagine">` (light) or
 * `<html data-theme="norton-reimagine-dark">` (dark), which activates the bundled Norton ODS design tokens
 * (see src/styles/tokens/*). Whether a member sees it is decided by the backend via
 * `primaryMember.showReimaginedExperience` on the member response (see utils.isReimagineThemeEligible);
 * the legacy Tailwind theme is retained for everyone else (Norton/CSP, partner/white-label, International).
 * Light vs dark follows the member's OS/browser color-scheme preference (prefers-color-scheme).
 *
 * Partner/white-label flows set their own `data-theme` after login (partnerUtils.loadPartnerTheme), which
 * continues to win over the ODS theme.
 *
 * Marketplace and credit-offers surfaces scope the same `norton-reimagine` tokens on a descendant wrapper
 * (`MarketplaceReimagineScope`) so the experience is always ODS there without retargeting `<html>` (legacy
 * nav unchanged). Marketplace is not shown to white-label members.
 */

export const REIMAGINE_THEME = 'norton-reimagine';
// TODO: Uncomment this when dark mode is implemented - AVNGR-7312
// export const REIMAGINE_THEME_DARK = 'norton-reimagine-dark';
export const REIMAGINE_THEME_DARK = 'norton-reimagine';

const REIMAGINE_THEMES: readonly string[] = [REIMAGINE_THEME, REIMAGINE_THEME_DARK];

/**
 * Whether `<html data-theme>` is currently a Reimagine (Norton ODS) theme value.
 * Used by Genie (and similar) to wait until App's `syncReimagineTheme` has applied before
 * reading computed CSS variables into `@fw/theme`.
 * @param doc - Document to read (defaults to the global document; injectable for tests).
 */
export function isReimagineDataThemeActive(doc: Document = document): boolean {
  const currentTheme = doc.documentElement?.getAttribute('data-theme');
  return Boolean(currentTheme && REIMAGINE_THEMES.includes(currentTheme));
}

/**
 * Whether the OS/browser is requesting a dark color scheme. Falls back to false (light) when
 * matchMedia is unavailable (e.g. older test environments).
 * @param win - Window to query (defaults to the global window; injectable for tests).
 */
export function prefersDarkColorScheme(win: Window = window): boolean {
  return Boolean(win && typeof win.matchMedia === 'function' && win.matchMedia('(prefers-color-scheme: dark)').matches);
}

/**
 * Reconciles the Reimagine theme on `<html>` once the member response is known (App.tsx). For eligible
 * members it applies the ODS light or dark theme based on the OS color-scheme preference, and switches
 * between them when that preference changes; for everyone else it removes the ODS theme so the legacy
 * theme applies. It only ever sets/clears the reimagine values, so partner and legacy-dark themes
 * already on `<html>` are left untouched.
 * @param isEligible - Result of utils.isReimagineThemeEligible(auth).
 * @param prefersDark - Whether the dark ODS theme should be used (defaults to the OS preference).
 * @param doc - Document to mutate (defaults to the global document; injectable for tests).
 */
export function syncReimagineTheme(
  isEligible: boolean,
  prefersDark: boolean = prefersDarkColorScheme(),
  doc: Document = document
): void {
  const htmlEl = doc.documentElement;
  if (!htmlEl) {
    return;
  }
  const currentTheme = htmlEl.getAttribute('data-theme');
  const reimagineOwnsTheme = !currentTheme || REIMAGINE_THEMES.includes(currentTheme);
  if (isEligible) {
    // Don't disturb a partner/legacy-dark theme that already owns the attribute.
    if (!reimagineOwnsTheme) {
      return;
    }
    const desiredTheme = prefersDark ? REIMAGINE_THEME_DARK : REIMAGINE_THEME;
    if (currentTheme !== desiredTheme) {
      htmlEl.setAttribute('data-theme', desiredTheme);
    }
    return;
  }
  // Not eligible: drop our own light/dark theme so the legacy theme applies, but never a partner theme.
  if (currentTheme && REIMAGINE_THEMES.includes(currentTheme)) {
    htmlEl.removeAttribute('data-theme');
  }
}
